// components/Pagination.js
import { useTheme } from '../../context/ThemeContext';

const Pagination = ({
  filteredItems,
  currentPage,
  itemsPerPage,
  indexOfFirstItem,
  indexOfLastItem,
  totalPages,
  paginate
}) => {
  const { isDarkMode } = useTheme();

  // Cores elegantes para dark/light mode
  const activeButtonBg = isDarkMode 
    ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/25' 
    : 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/40';
  
  const inactiveButtonBg = isDarkMode 
    ? 'bg-gray-700/80 hover:bg-gray-600 text-gray-200 border-gray-600 backdrop-blur-sm' 
    : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200 shadow-sm';
  
  const containerBg = isDarkMode ? 'bg-gray-800/95 backdrop-blur-sm' : 'bg-white/95 backdrop-blur-sm';
  const textColor = isDarkMode ? 'text-gray-300' : 'text-gray-600';
  const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  // Calcular números de página para mostrar
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Aumentei para mostrar mais páginas
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 3);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      // Ajusta se estiver perto do final
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  if (totalPages <= 1) {
    return null;
  }

  const pageNumbers = getPageNumbers();

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t ${borderColor} ${containerBg} rounded-b-2xl`}>
      {/* Informações com estilo mais elegante */}
      <div className={`text-sm mb-4 sm:mb-0 ${textColor} font-medium`}>
        Mostrando <span className="text-green-500 font-semibold">{indexOfFirstItem + 1}</span> a{' '}
        <span className="text-green-500 font-semibold">
          {Math.min(indexOfLastItem, filteredItems.length)}
        </span> de{' '}
        <span className="text-green-500 font-semibold">{filteredItems.length}</span> resultados
        <span className="text-xs ml-2 opacity-70">
          • Página {currentPage} de {totalPages}
        </span>
      </div>

      {/* Números de página elegantes e com tamanho uniforme */}
      <div className="flex items-center space-x-2">
        {pageNumbers.map(number => (
          <button
            key={number}
            onClick={() => paginate(number)}
            className={`
              w-10 h-10 rounded-xl font-medium text-sm transition-all duration-300 ease-in-out
              border transform hover:scale-105 active:scale-95
              flex items-center justify-center
              ${currentPage === number 
                ? `${activeButtonBg} border-transparent ring-2 ring-green-400/30 font-semibold` 
                : `${inactiveButtonBg} border-gray-300 dark:border-gray-600 hover:border-green-300 dark:hover:border-green-700`
              }
              ${currentPage === number ? 'shadow-lg' : 'shadow'}
            `}
            aria-current={currentPage === number ? 'page' : undefined}
          >
            {number}
          </button>
        ))}
        
        {/* Indicador visual quando há mais páginas */}
        {pageNumbers[pageNumbers.length - 1] < totalPages && (
          <span className={`text-xs ${textColor} opacity-60 px-2`}>
            ...{totalPages}
          </span>
        )}
      </div>
    </div>
  );
};

export default Pagination;